import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import UnifiedLoader from '@/components/ui/UnifiedLoader';
import DelayedLoader from '@/components/ui/DelayedLoader';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute - Wraps routes that require admin role
 * 
 * Two-phase check:
 * 1. Authentication: redirects to /auth if not logged in (same as ProtectedRoute)
 * 2. Authorization: redirects to / if authenticated but not admin
 * 
 * Uses DelayedLoader to avoid flash during fast auth/role checks.
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminAccess();
  const location = useLocation();

  // Phase 1: Auth check
  if (authLoading) {
    return (
      <DelayedLoader delay={500}>
        <UnifiedLoader mode="fullscreen" text="Loading" />
      </DelayedLoader>
    );
  }

  if (!user) {
    const currentPath = location.pathname + location.search;
    const redirect = encodeURIComponent(currentPath);
    return <Navigate to={`/auth?redirect=${redirect}`} replace />;
  }

  // Phase 2: Admin role check
  if (adminLoading) {
    return (
      <DelayedLoader delay={500}>
        <UnifiedLoader mode="fullscreen" text="Loading" />
      </DelayedLoader>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
